/** @format */
import { css } from 'styled-components';

import {
    mobile,
    safeObjectFit,
    smallTablet,
} from '@toucan/client/global/application';

import { ContentCollectionVariant } from '@toucan/client/core/contentful';

import { ContentCollectionProps } from './index';

/**
 * Base styles for the content collection component
 *
 * @param {ContentCollectionProps} props The content collection properties object
 * */
const baseStyles = (props: ContentCollectionProps) => css`
    margin-bottom: var(--layout-spacing-72);

    .content-collection-heading {
        text-align: center;
        margin-bottom: var(--layout-spacing-48);
    }

    .content-collection-items {
        margin: 0;
        padding: 0;
        list-style: none;
    }

    .content-collection-item-image {
        overflow: hidden;
    }

    ${mobile(
        css`
            margin-bottom: 3.6rem;
        `
    )}
`;

/**
 * Variant styles for the content collection component
 *
 * @param {ContentCollectionProps} props The content collection properties object
 * */
const variantStyles = ({ variant, items }: ContentCollectionProps) => {
    switch (variant) {
        case ContentCollectionVariant.THREE_COL_CIRCULAR_IMAGES:
            return css`
                font-size: 1.2rem;

                .content-collection-items {
                    display: -ms-grid;
                    -ms-grid-columns: 1fr var(--layout-spacing-48) 1fr
                        var(--layout-spacing-48) 1fr;

                    display: grid;
                    grid-gap: var(--layout-spacing-48);
                    grid-template-columns: 1fr 1fr 1fr;
                }

                .content-collection-item {
                    text-align: center;

                    ${items.map((item, i) => {
                        const columnIndex = i % 3;
                        const columnPosition = columnIndex * 2 + 1;
                        const rowIndex = Math.floor(i / 3);
                        const rowPosition = rowIndex * 2 + 1;
                        return css`
                            &:nth-child(${i + 1}) {
                                -ms-grid-column: ${columnPosition};
                                -ms-grid-row: ${rowPosition};
                            }
                        `;
                    })};
                }

                .content-collection-item-image {
                    border-radius: 50%;
                    width: 12rem;
                    height: 12rem;
                    margin: 0 auto 1.2rem;
                    position: relative;

                    picture {
                        position: absolute;
                        top: 0;
                        right: 0;
                        bottom: 0;
                        left: 0;
                    }

                    img {
                        height: 100%;
                        width: 100%;
                        ${safeObjectFit('cover')};
                    }
                }

                ${smallTablet(
                    css`
                        .content-collection-items {
                            -ms-grid-columns: 1fr var(--layout-spacing-24) 1fr
                                var(--layout-spacing-24) 1fr;
                            grid-gap: var(--layout-spacing-24);
                        }
                    `
                )}

                ${mobile(
                    css`
                        .content-collection-items {
                            display: block;
                        }

                        .content-collection-item {
                            max-width: 35rem;
                            margin: 0 auto var(--layout-spacing-24);
                        }
                    `
                )}
            `;
        case ContentCollectionVariant.TWO_COL_CARDS:
            return css`
                .content-collection-items {
                    -ms-grid-columns: 1fr var(--layout-spacing-24) 1fr;
                    display: -ms-grid;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    grid-column-gap: var(--layout-spacing-24);
                }

                .content-collection-item {
                    display: flex;
                    flex-direction: column;
                    margin-bottom: var(--layout-spacing-24);
                    box-shadow: 0 0.25rem 1.25rem rgba(17, 23, 80, 0.1);
                    border-radius: 0.5rem;
                    overflow: hidden;
                    background-color: var(--colors-surface-primary);

                    ${items.map((item, i) => {
                        const columnIndex = i % 2;
                        const columnPosition = columnIndex * 2 + 1;
                        const rowIndex = Math.floor(i / 2);
                        const rowPosition = rowIndex + 1;
                        return css`
                            &:nth-child(${i + 1}) {
                                -ms-grid-column: ${columnPosition};
                                -ms-grid-row: ${rowPosition};
                            }
                        `;
                    })};
                }

                .content-collection-item-content {
                    padding: 0.5rem 1.5rem 2rem 1.5rem;

                    h3 {
                        margin-bottom: 1.5rem;
                    }
                }

                .content-collection-item-action {
                    margin-top: 1.5rem;
                }

                .content-collection-item-image {
                    flex-shrink: 0;
                    display: block;
                    max-width: 100%;
                }

                ${mobile(
                    css`
                        .content-collection-items {
                            display: block;
                        }
                    `
                )}
            `;

        case ContentCollectionVariant.TWO_COL_IMAGES_LEFT:
        default:
            return css`
                font-size: 1.2rem;
                .content-collection-items {
                    -ms-grid-columns: 1fr var(--layout-spacing-24) 1fr;
                    display: -ms-grid;
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    grid-column-gap: var(--layout-spacing-24);
                }
                .content-collection-item {
                    display: flex;
                    margin-bottom: var(--layout-spacing-24);

                    ${items.map((item, i) => {
                        const columnIndex = i % 2;
                        const columnPosition = columnIndex * 2 + 1;
                        const rowIndex = Math.floor(i / 2);
                        const rowPosition = rowIndex + 1;
                        return css`
                            &:nth-child(${i + 1}) {
                                -ms-grid-column: ${columnPosition};
                                -ms-grid-row: ${rowPosition};
                            }
                        `;
                    })};
                }

                .content-collection-item-image {
                    width: 4rem;
                    flex-shrink: 0;
                    margin-right: 1.6rem;
                }

                ${mobile(
                    css`
                        .content-collection-items {
                            display: block;
                        }

                        .content-collection-item-image {
                            width: 2.5rem;
                        }
                    `
                )}
            `;
    }
};

/**
 * The content collection component styles
 *
 * @param {ContentCollectionProps} props The content collection properties object
 * */
export const styles = (props: ContentCollectionProps) => css`
    ${baseStyles(props)}
    ${variantStyles(props)}
`;
